import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  ClickAwayListener,
  Fade,
  IconButton,
  Paper,
  Popper,
  Stack,
  Typography,
} from "@mui/material";

import HomeIcon from "@mui/icons-material/Home";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GroupIcon from "@mui/icons-material/Group";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";

import AuthApi from "../../features/auth/api/authApi";
import { useAuth } from "../providers/AuthProvider";

const PROFILE_POPUP_MS = 3500;

function clearAuthStorage() {
  Object.keys(localStorage)
    .filter((k) => k.startsWith("sabi_"))
    .forEach((k) => localStorage.removeItem(k));

  ["access_token", "refresh_token", "token"].forEach((k) => localStorage.removeItem(k));

  Object.keys(sessionStorage)
    .filter((k) => k.startsWith("sabi_"))
    .forEach((k) => sessionStorage.removeItem(k));
}

function safeParseJson(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function formatUserRole(role) {
  const value = String(role || "").toUpperCase();

  if (value === "OWNER") return "Owner";
  if (value === "CONSULTORA") return "Consultora";
  if (value === "MANAGER") return "Manager";
  if (value === "EMPLOYEE") return "Empleado";

  if (!value) return "-";
  return value.charAt(0) + value.slice(1).toLowerCase();
}

function readProfileInfoFromStorage() {
  const rawUser = safeParseJson(localStorage.getItem("sabi_user"));
  const rawEmpresa = safeParseJson(localStorage.getItem("sabi_empresa"));
  const empresaNombre = localStorage.getItem("sabi_empresa_nombre");

  return {
    displayName:
      rawUser?.display_name ||
      rawUser?.displayName ||
      rawUser?.name ||
      "-",
    userRole: formatUserRole(rawUser?.user_role || rawUser?.userRole),
    companyName:
      empresaNombre ||
      rawEmpresa?.name ||
      rawEmpresa?.nombre ||
      "-",
  };
}

export default function SideBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location?.pathname || "";

  const { logout: logoutCtx } = useAuth();

  const [loggingOut, setLoggingOut] = useState(false);

  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileInfo, setProfileInfo] = useState(() => readProfileInfoFromStorage());

  const profileTimerRef = useRef(null);

  const clearProfileTimer = useCallback(() => {
    if (profileTimerRef.current) {
      clearTimeout(profileTimerRef.current);
      profileTimerRef.current = null;
    }
  }, []);

  const scheduleProfileClose = useCallback(() => {
    clearProfileTimer();
    profileTimerRef.current = setTimeout(() => {
      setProfileOpen(false);
    }, PROFILE_POPUP_MS);
  }, [clearProfileTimer]);

  useEffect(() => {
    return () => {
      clearProfileTimer();
    };
  }, [clearProfileTimer]);

  const hydrateProfileInfo = useCallback(async () => {
    const localInfo = readProfileInfoFromStorage();
    setProfileInfo(localInfo);

    const needsFetch =
      !localInfo.displayName ||
      localInfo.displayName === "-" ||
      !localInfo.companyName ||
      localInfo.companyName === "-";

    if (!needsFetch) return;

    try {
      const me = await AuthApi.getMe();

      const currentLicenseId = me?.context?.licenseId;
      const matchedLicense =
        (me?.licenses || []).find((l) => Number(l.id) === Number(currentLicenseId)) ||
        me?.licenses?.[0];

      setProfileInfo({
        displayName:
          me?.usuario?.display_name ||
          me?.usuario?.displayName ||
          localInfo.displayName ||
          "-",
        userRole: formatUserRole(me?.usuario?.user_role || me?.usuario?.userRole),
        companyName:
          localStorage.getItem("sabi_empresa_nombre") ||
          matchedLicense?.name ||
          matchedLicense?.nombre ||
          localInfo.companyName ||
          "-",
      });
    } catch {
      // dejamos lo de localStorage si falla
    }
  }, []);

  const handleProfileClick = useCallback(
    async (event) => {
      setProfileAnchorEl(event.currentTarget);
      setProfileOpen(true);
      scheduleProfileClose();
      await hydrateProfileInfo();
    },
    [hydrateProfileInfo, scheduleProfileClose]
  );

  const handleCloseProfile = useCallback(() => {
    clearProfileTimer();
    setProfileOpen(false);
  }, [clearProfileTimer]);

  const handleLogout = useCallback(async () => {
    if (loggingOut) return;

    setLoggingOut(true);
    try {
      try {
        await AuthApi.logout?.();
      } catch {
        // ignore
      }

      try {
        logoutCtx?.();
      } catch {
        // ignore
      }

      clearAuthStorage();
      navigate("/login", { replace: true });
    } finally {
      setLoggingOut(false);
    }
  }, [loggingOut, logoutCtx, navigate]);

  const goInicio = useCallback(() => {
    navigate("/inicio");
  }, [navigate]);

  const goRrhh = useCallback(() => {
    navigate("/rrhh/dashboard");
  }, [navigate]);

  const goTesoreria = useCallback(() => {
    navigate("/tesoreria");
  }, [navigate]);

  const goStock = useCallback(() => {
    navigate("/stock");
  }, [navigate]);

  const goConfiguraciones = useCallback(() => {
    navigate("/configuraciones");
  }, [navigate]);

  const isInicioActive = useMemo(() => pathname.startsWith("/inicio"), [pathname]);
  const isStockActive = useMemo(() => pathname.startsWith("/stock"), [pathname]);
  const isRrhhActive = useMemo(() => pathname.startsWith("/rrhh"), [pathname]);
  const isTesoreriaActive = useMemo(
    () => pathname.startsWith("/tesoreria"),
    [pathname]
  );
  const isSettingsActive = useMemo(
    () => pathname.startsWith("/configuraciones"),
    [pathname]
  );

  const handleOpenSettings = useCallback(() => {
    goConfiguraciones();
  }, [goConfiguraciones]);

  const navBtnSx = (active) => ({
    width: 36,
    height: 36,
    bgcolor: active ? "common.white" : "primary.400",
    boxShadow: active
      ? "inset 0px 0px 3px rgba(192, 192, 201, 0.35), 0px 10px 22px rgba(2, 8, 23, 0.10)"
      : "none",
    border: active ? "1px solid rgba(15,23,42,.08)" : "1px solid transparent",
    transition: "all .15s ease",
    "&:hover": {
      bgcolor: active ? "neutral.50" : "primary.500",
      transform: "translateY(-1px)",
    },
  });

  const navIconSx = (active) => ({
    width: 32,
    height: 32,
    color: active ? "primary.500" : "neutral.50",
    transition: "all .15s ease",
  });

  return (
    <ClickAwayListener onClickAway={handleCloseProfile}>
      <Box
        sx={{
          width: { xs: 72, md: 90 },
          p: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Stack
          spacing={3}
          sx={{
            width: "100%",
            minHeight: "calc(88vh - 32px)",
            justifyContent: "space-between",
            position: "sticky",
          }}
        >
          {/* TOP pill */}
          <Stack
            spacing={2.5}
            sx={{
              alignItems: "center",
              py: 2.5,
              bgcolor: "neutral.150",
              borderRadius: "103.5px",
              boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
            }}
          >
            <IconButton onClick={goInicio} sx={navBtnSx(isInicioActive)}>
              <HomeIcon sx={navIconSx(isInicioActive)} />
            </IconButton>
          
            <IconButton onClick={goStock} sx={navBtnSx(isStockActive)}>
              <Inventory2Icon sx={navIconSx(isStockActive)} />
            </IconButton>

            <IconButton onClick={goTesoreria} sx={navBtnSx(isTesoreriaActive)}>
              <AccountBalanceWalletIcon sx={navIconSx(isTesoreriaActive)} />
            </IconButton>
          
            <IconButton onClick={goRrhh} sx={navBtnSx(isRrhhActive)}>
              <GroupIcon sx={navIconSx(isRrhhActive)} />
            </IconButton>
          </Stack>

          {/* BOTTOM pill */}
          <Stack
            spacing={2.5}
            sx={{
              alignItems: "center",
              py: 2.5,
              bgcolor: "neutral.150",
              borderRadius: "103.5px",
              boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
              justifyContent: "space-between",
            }}
          >
            <IconButton onClick={handleOpenSettings} sx={navBtnSx(isSettingsActive)}>
              <SettingsIcon sx={navIconSx(isSettingsActive)} />
            </IconButton>
            
            <IconButton
              onClick={handleLogout}
              disabled={loggingOut}
              sx={{ width: 36, height: 36, bgcolor: "primary.400" }}
            >
              <LogoutIcon sx={{ width: 32, height: 32, color: "neutral.50" }} />
            </IconButton>


            <IconButton
              onClick={handleProfileClick}
              sx={{
                width: 36,
                height: 36,
                bgcolor: profileOpen ? "common.white" : "primary.400",
                boxShadow: profileOpen
                  ? "inset 0px 0px 3px rgba(192, 192, 201, 0.35), 0px 8px 18px rgba(2, 8, 23, 0.10)"
                  : "none",
                transition: "all .15s ease",
                "&:hover": {
                  bgcolor: profileOpen ? "neutral.50" : "primary.500",
                },
              }}
            >
              <PersonIcon
                sx={{
                  width: 32,
                  height: 32,
                  color: profileOpen ? "primary.500" : "neutral.50",
                }}
              />
            </IconButton>

            <Popper
              open={profileOpen}
              anchorEl={profileAnchorEl}
              placement="right"
              transition
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [10, 0],
                  },
                },
              ]}
              sx={{ zIndex: 1400 }}
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={180}>
                  <Paper
                    elevation={0}
                    sx={{
                      minWidth: 220,
                      p: 1,
                      borderRadius: "18px",
                      bgcolor: "neutral.50",
                      boxShadow: "0 12px 30px rgba(2, 8, 23, 0.16)",
                      border: "1px solid rgba(15, 23, 42, 0.08)",
                      position: "relative",
                      bottom: 16,
                      left: 10,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: -7,
                        top: "70%",
                        width: 12,
                        height: 12,
                        bgcolor: "neutral.50",
                        transform: "translateY(-50%) rotate(45deg)",
                        borderLeft: "1px solid rgba(15, 23, 42, 0.08)",
                        borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                      },
                    }}
                  >
                    <Stack spacing={0.8}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          pl: 1,
                          fontWeight: 800,
                          color: "primary.900",
                          lineHeight: 1.2,
                        }}
                      >
                        {profileInfo.displayName || "-"}
                      </Typography>

                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: "12px",
                          bgcolor: "secondary.50",
                          boxShadow: "inset 0px 0px 2px rgba(117, 117, 138, 0.35)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "neutral.700",
                            mb: 0.15,
                          }}
                        >
                          Rol
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            fontWeight: 700,
                            color: "primary.900",
                          }}
                        >
                          {profileInfo.userRole || "-"}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          px: 1,
                          py: 0.6,
                          borderRadius: "12px",
                          bgcolor: "secondary.50",
                          boxShadow: "inset 0px 0px 2px rgba(117, 117, 138, 0.35)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "neutral.700",
                            mb: 0.15,
                          }}
                        >
                          Empresa
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            fontWeight: 700,
                            color: "primary.900",
                            lineHeight: 1.2,
                          }}
                        >
                          {profileInfo.companyName || "-"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Fade>
              )}
            </Popper>
          </Stack>
        </Stack>
      </Box>
    </ClickAwayListener>
  );
}
